/* Universal Travel Packing Checklist template — powers the interactive
   "What Should I Pack?" tool (/packing). Items with no tags apply to every trip;
   tagged items appear only when the matching trip type is selected. */

export const TRIP_TYPES = [
  ["international", "International"], ["family", "Family / Children"], ["pet", "Pet"],
  ["cruise", "Cruise"], ["road_trip", "Road Trip"], ["business", "Business"],
  ["senior", "Senior"], ["winter", "Winter"], ["beach", "Beach"], ["adventure", "Adventure"],
];

const I = (id, label, tags) => ({ id, label, tags: tags || [] });

export const NEVER_CHECK = [
  "Essential medications", "Passport and important documents", "Cash and credit cards",
  "Electronics you need", "Power banks / spare lithium batteries", "Irreplaceable items",
  "Keys", "One emergency change of clothes", "Essential medical equipment", "Glasses / contact lenses",
];

export const SECTIONS = [
  { id: "documents", title: "Essential Documents & Money", tip: "Keep your passport, money, cards and essential documents in your personal item — on your person, never in checked luggage.", items: [
    I("doc-passport", "Passport"),
    I("doc-visa", "Visa / ETA / ESTA or other entry authorization, if required", ["international"]),
    I("doc-license", "Driver's license / government ID"),
    I("doc-boarding", "Boarding passes"),
    I("doc-hotel", "Hotel / accommodation confirmations"),
    I("doc-rental", "Rental-car confirmation", ["road_trip"]),
    I("doc-insurance", "Travel insurance information"),
    I("doc-health-card", "Health insurance card"),
    I("doc-emergency", "Emergency-contact information"),
    I("doc-copies", "Copies of important documents"),
    I("doc-digital", "Digital copies stored securely"),
    I("doc-cards", "Credit / debit cards"),
    I("doc-currency", "Small amount of local currency", ["international"]),
    I("doc-cash", "Emergency cash"),
    I("doc-pen", "Pen(s) — very useful for immigration/customs forms"),
    I("doc-notebook", "Small notebook"),
    I("doc-idp", "International driving permit, if needed", ["international", "road_trip"]),
    I("doc-embassy", "Embassy / consulate information", ["international"]),
    I("doc-vaccine", "Vaccination documentation, if required", ["international"]),
  ]},
  { id: "health", title: "Health & Personal Protection", items: [
    I("hp-masks", "Face masks"),
    I("hp-sanitizer", "Hand sanitizer"),
    I("hp-wipes", "Disinfecting wipes"),
    I("hp-tissues", "Tissues / handkerchief"),
    I("hp-firstaid", "Small first-aid kit"),
    I("hp-bandaids", "Bandages / blister treatment"),
    I("hp-antiseptic", "Antiseptic wipes"),
    I("hp-sunscreen", "Sunscreen", ["beach", "adventure", "cruise"]),
    I("hp-lipbalm", "Lip balm"),
    I("hp-lotion", "Small bottle of body/skin lotion"),
    I("hp-repellent", "Insect repellent, where appropriate", ["beach", "adventure"]),
    I("hp-aftersun", "Aloe / after-sun lotion", ["beach", "cruise"]),
    I("hp-compression", "Compression socks / calf sleeves (ask a clinician if unsure)", ["international", "senior"]),
  ]},
  { id: "meds", title: "Medicines & Health Supplies", tip: "Keep essential medications in your carry-on — never in checked baggage.", items: [
    I("md-rx", "Prescription medicines"),
    I("md-otc", "Regular over-the-counter medicines"),
    I("md-pain", "Pain / headache medicine"),
    I("md-allergy", "Allergy medicine"),
    I("md-motion", "Motion-sickness medicine", ["cruise", "road_trip"]),
    I("md-stomach", "Stomach / indigestion / diarrhea remedies"),
    I("md-cold", "Cold / flu remedies"),
    I("md-devices", "Personal medical devices", ["senior"]),
    I("md-docs", "Medical documentation, where appropriate", ["international", "senior"]),
    I("md-glucose", "Quick-energy food: glucose tablets, hard candy or juice"),
  ]},
  { id: "hydration", title: "Water & Hydration", tip: "Empty your refillable bottle before security, refill after.", items: [
    I("wt-bottle", "Refillable / collapsible water bottle"),
    I("wt-electrolytes", "Electrolyte packets, if appropriate", ["adventure", "beach"]),
    I("wt-cup", "Reusable travel cup"),
  ]},
  { id: "emergency", title: "\"You Never Know\" Emergency Kit", tip: "Takes little space — a lifesaver during delays, spills, lost luggage or unexpected overnights.", items: [
    I("em-tp", "Folded toilet paper / napkins"),
    I("em-wetwipes", "Wet wipes"),
    I("em-ziplock", "Zip-lock bags / small trash bags"),
    I("em-towels", "Paper towels"),
    I("em-underwear", "Extra underwear + extra socks"),
    I("em-clothes", "ONE complete change of clothes (shirt, pants, underwear, socks)"),
    I("em-poncho", "Lightweight rain poncho"),
    I("em-flashlight", "Small flashlight"),
    I("em-pins", "Safety pins / small sewing kit"),
  ]},
  { id: "weather", title: "Sun & Weather Protection", items: [
    I("wx-hat", "Hat / cap"),
    I("wx-sunglasses", "Sunglasses"),
    I("wx-rain", "Light rain jacket / compact umbrella"),
    I("wx-gloves", "Gloves for cold destinations", ["winter"]),
    I("wx-warm", "Warm layer / sweater / scarf", ["winter", "international"]),
    I("wx-cooling", "Cooling towel for hot climates", ["beach"]),
  ]},
  { id: "comfort", title: "Sleep & Comfort", tip: "Earplugs + eye mask + headphones + neck pillow make long flights dramatically better.", items: [
    I("cf-earplugs", "Earplugs"),
    I("cf-mask", "Sleeping mask"),
    I("cf-pillow", "Travel pillow"),
    I("cf-blanket", "Small blanket / scarf"),
    I("cf-headphones", "Noise-canceling headphones"),
    I("cf-socks", "Comfortable socks and clothing"),
  ]},
  { id: "electronics", title: "Electronics", tip: "A plug adapter changes the plug shape — it does NOT convert voltage. Check your devices support the destination voltage.", items: [
    I("el-phone", "Smartphone + charger + cables"),
    I("el-wall", "Wall charger / USB adapters"),
    I("el-adapter", "Travel plug adapter for your destinations", ["international"]),
    I("el-converter", "Voltage converter, if actually required", ["international"]),
    I("el-powerbank", "Power bank — CABIN ONLY, keep accessible, never checked"),
    I("el-laptop", "Laptop / tablet / e-reader", ["business"]),
    I("el-headphones", "Headphones / earbuds / Bluetooth accessories"),
    I("el-camera", "Camera + batteries + memory cards", ["adventure", "cruise", "beach"]),
    I("el-organizer", "Small cable organizer"),
    I("el-offline", "Offline maps + translation app / offline language pack", ["international", "road_trip"]),
  ]},
  { id: "toiletries", title: "Toiletries", items: [
    I("tl-toothbrush", "Toothbrush + toothpaste"),
    I("tl-deodorant", "Deodorant"),
    I("tl-comb", "Comb / brush"),
    I("tl-razor", "Razor / shaving supplies"),
    I("tl-shampoo", "Travel-size shampoo / conditioner / soap"),
    I("tl-face", "Face wash / moisturizer"),
    I("tl-feminine", "Feminine hygiene products"),
    I("tl-clippers", "Nail clippers (security rules for sharp objects vary — check if unsure)"),
  ]},
  { id: "kit24", title: "24-Hour Emergency Pouch (goes in your personal item)", tip: "If your checked bag disappears, you've still got the basics for the first day.", items: [
    I("k24-basics", "Mini toothbrush + toothpaste + deodorant"),
    I("k24-hygiene", "Hand sanitizer + wet wipes + tissues"),
    I("k24-skin", "Lotion + lip balm + comb"),
    I("k24-meds", "Basic medicines"),
    I("k24-clothes", "Underwear + socks + mask"),
  ]},
  { id: "vision", title: "Vision & Hearing", tip: "Never put irreplaceable glasses or hearing devices in checked luggage.", items: [
    I("vs-glasses", "Prescription glasses + backup pair"),
    I("vs-contacts", "Contact lenses + case + solution"),
    I("vs-hearing", "Hearing aids + batteries/charger", ["senior"]),
  ]},
  { id: "security", title: "Security & Privacy", tip: "Don't display your full home address prominently on external luggage tags.", items: [
    I("sc-locks", "Luggage locks, where appropriate"),
    I("sc-tags", "Luggage tags"),
    I("sc-tracker", "AirTag / luggage tracker"),
    I("sc-rfid", "RFID / card protection, if desired"),
    I("sc-pouch", "Secure travel pouch / neck wallet / money belt — keep passport, cash and cards ON YOUR PERSON"),
  ]},
  { id: "children", title: "Traveling With Children", tip: "Pack more snacks, wipes and clothes than you think you'll need.", tags: ["family"], items: [
    I("ch-diapers", "Diapers + wipes", ["family"]),
    I("ch-formula", "Formula / breast milk + bottles + baby food", ["family"]),
    I("ch-pacifier", "Pacifier + favorite toy + comfort blanket", ["family"]),
    I("ch-snacks", "Snacks + age-appropriate candy/lollipop (ear pressure on descent)", ["family"]),
    I("ch-clothes", "Extra children's clothing", ["family"]),
    I("ch-tablet", "Tablet + child headphones + coloring book + small games", ["family"]),
    I("ch-meds", "Child medication", ["family"]),
    I("ch-stroller", "Stroller (check the airline's gate-check policy)", ["family"]),
    I("ch-carseat", "Approved child restraint / car seat, if applicable", ["family"]),
  ]},
  { id: "pets", title: "Traveling With Pets", tip: "For international pet travel, check destination-country requirements weeks or months in advance.", tags: ["pet"], items: [
    I("pt-carrier", "Airline-approved carrier", ["pet"]),
    I("pt-id", "Pet identification + microchip information", ["pet"]),
    I("pt-vax", "Vaccination records + health certificate if required", ["pet"]),
    I("pt-food", "Food + water container", ["pet"]),
    I("pt-leash", "Leash / harness", ["pet"]),
    I("pt-waste", "Waste bags + absorbent pads", ["pet"]),
    I("pt-meds", "Pet medication + comfort item", ["pet"]),
  ]},
  { id: "road", title: "Road Trip Additions", tags: ["road_trip"], items: [
    I("rd-registration", "Vehicle registration + insurance + roadside-assistance info", ["road_trip"]),
    I("rd-mount", "Phone mount + car charger", ["road_trip"]),
    I("rd-kit", "Emergency kit: flashlight, jumper cables, tire-pressure gauge", ["road_trip"]),
    I("rd-supplies", "Water + blankets + snacks", ["road_trip"]),
    I("rd-maps", "Paper maps / offline maps", ["road_trip"]),
  ]},
  { id: "cruise", title: "Cruise Additions", tags: ["cruise"], items: [
    I("cr-docs", "Cruise documents", ["cruise"]),
    I("cr-seasick", "Seasickness remedies / motion-sickness bands", ["cruise"]),
    I("cr-daybag", "Small day bag for ports", ["cruise"]),
    I("cr-formal", "Formal / evening clothing, if required", ["cruise"]),
    I("cr-evac", "Travel insurance + medical-evacuation information", ["cruise"]),
  ]},
];

/* Personal-item survival strategy — always displayed */
export const PERSONAL_ITEM = [
  "Passport/ID", "Wallet", "Phone", "Medication", "Glasses", "Travel documents", "Power bank",
  "Charger", "One change of clothes", "Basic toiletries", "Essential medical supplies", "Snacks",
  "Water bottle", "Earplugs", "Sleep mask", "Pen", "Emergency supplies",
];

export const GOLDEN_RULE = "If losing your checked suitcase would cause a serious problem during the next 24 hours, put that item in your personal item or carry-on instead.";
